import { useState } from 'react';
import { normalizeAjaxUrl } from '@/utils/normalizeUrl';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  User,
  Mail,
  Phone,
  Lock,
  BookOpen,
  Building2,
  Award,
  FileText,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  Sparkles,
  GraduationCap,
  Clock,
  Loader2
} from 'lucide-react';

interface InstructorRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type RegStep = 'signup' | 'apply' | 'success' | 'pending';

const subjectOptions = [
  { value: 'bangla', label: 'বাংলা' },
  { value: 'english', label: 'ইংরেজি' },
  { value: 'math', label: 'গণিত' },
  { value: 'physics', label: 'পদার্থবিজ্ঞান' },
  { value: 'chemistry', label: 'রসায়ন' },
  { value: 'biology', label: 'জীববিজ্ঞান' },
  { value: 'ict', label: 'তথ্য ও যোগাযোগ প্রযুক্তি' },
  { value: 'general-science', label: 'সাধারণ বিজ্ঞান' },
  { value: 'accounting', label: 'হিসাববিজ্ঞান' },
  { value: 'business', label: 'ব্যবসায় উদ্যোগ' },
  { value: 'economics', label: 'অর্থনীতি' },
  { value: 'religion', label: 'ইসলাম শিক্ষা' },
  { value: 'history', label: 'ইতিহাস ও সামাজিক বিজ্ঞান' },
  { value: 'geography', label: 'ভূগোল ও পরিবেশ' },
  { value: 'other', label: 'অন্যান্য' },
];

const experienceOptions = [
  { value: '0-1', label: '০-১ বছর' },
  { value: '1-3', label: '১-৩ বছর' },
  { value: '3-5', label: '৩-৫ বছর' },
  { value: '5-10', label: '৫-১০ বছর' },
  { value: '10+', label: '১০+ বছর' },
];

export default function InstructorRegistrationModal({ isOpen, onClose }: InstructorRegistrationModalProps) {
  const [step, setStep] = useState<RegStep>('signup');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Step 1 fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');

  // Step 2 fields
  const [subject, setSubject] = useState('');
  const [institution, setInstitution] = useState('');
  const [experience, setExperience] = useState('');
  const [qualification, setQualification] = useState('');
  const [bio, setBio] = useState('');

  const handleAutofill = () => {
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    setName(`Test Instructor ${randomNum}`);
    setEmail(`instructor${randomNum}@shuddhoshikha.com`);
    setPhone(`017${randomNum}12345`);
    setPassword('instructor123');
    
    setSubject('physics');
    setInstitution('Shuddhoshikha Academy');
    setExperience('3-5');
    setQualification('M.Sc in Physics');
    setBio('I am an experienced instructor passionate about physics education.');
  };

  const resetForm = () => {
    const regObj = (window as any).shInstructorReg;
    setStep(regObj && regObj.is_logged_in ? 'apply' : 'signup');
    setError('');
    setName('');
    setEmail('');
    setPhone('');
    setPassword('');
    setSubject('');
    setInstitution('');
    setExperience('');
    setQualification('');
    setBio('');
    setLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const executeApiCall = async (action: string, data: Record<string, string>) => {
    const regObj = (window as any).shInstructorReg;
    if (regObj && regObj.ajax_url && regObj.nonce) {
      const formData = new FormData();
      formData.append('action', action);
      formData.append('nonce', regObj.nonce);
      Object.entries(data).forEach(([key, val]) => formData.append(key, val));

      const response = await fetch(normalizeAjaxUrl(regObj.ajax_url), {
        method: 'POST',
        body: formData,
      });
      const res = await response.json();
      if (!res.success) {
        throw new Error(res.data?.message || 'অনাকাঙ্ক্ষিত ত্রুটি ঘটেছে।');
      }
      return res.data;
    } else {
      // Mock offline / Vite dev mode
      await new Promise(resolve => setTimeout(resolve, 800));
      if (action === 'sh_instructor_register') {
        console.log('[MOCK] Instructor registration step 1:', data);
        return { message: 'অ্যাকাউন্ট সফলভাবে তৈরি হয়েছে।', user_id: 999 };
      } else if (action === 'sh_instructor_apply') {
        console.log('[MOCK] Instructor application step 2:', data);
        return { message: 'আবেদন সফলভাবে জমা হয়েছে। অ্যাডমিন অনুমোদনের অপেক্ষায় আছে।' };
      }
      return {};
    }
  };

  // Set initial step based on whether user is already logged in
  useState(() => {
    const regObj = (window as any).shInstructorReg;
    if (regObj && regObj.is_logged_in) {
      setStep('apply');
    }
  });

  const handleStep1Submit = async () => {
    setError('');
    if (!name.trim()) { setError('পূর্ণ নাম দিন।'); return; }
    if (!email.trim() || !email.includes('@')) { setError('সঠিক ইমেইল ঠিকানা দিন।'); return; }
    if (!phone.trim() || phone.length < 11) { setError('সঠিক ফোন নম্বর দিন।'); return; }
    if (!password || password.length < 6) { setError('পাসওয়ার্ড কমপক্ষে ৬ অক্ষরের হতে হবে।'); return; }

    setLoading(true);
    try {
      const res = await executeApiCall('sh_instructor_register', { name, email, phone, password });
      if (res && res.nonce) {
        const regObj = (window as any).shInstructorReg;
        if (regObj) {
          regObj.nonce = res.nonce;
        }
      }
      setStep('apply');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Submit = async () => {
    setError('');
    if (!subject) { setError('বিষয় নির্বাচন করুন।'); return; }
    if (!institution.trim()) { setError('প্রতিষ্ঠানের নাম দিন।'); return; }
    if (!experience) { setError('অভিজ্ঞতা নির্বাচন করুন।'); return; }
    if (!qualification.trim()) { setError('শিক্ষাগত যোগ্যতা দিন।'); return; }
    if (!bio.trim()) { setError('আপনার সম্পর্কে কিছু লিখুন।'); return; }

    setLoading(true);
    try {
      await executeApiCall('sh_instructor_apply', { subject, institution, experience, qualification, bio });
      setStep('success');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const backdropVariant = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const modalVariant = {
    hidden: { opacity: 0, scale: 0.92, y: 30 },
    visible: { opacity: 1, scale: 1, y: 0, transition: { type: 'spring' as const, damping: 25, stiffness: 350 } },
    exit: { opacity: 0, scale: 0.92, y: 30 },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          variants={backdropVariant}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />

          {/* Modal */}
          <motion.div
            className="relative w-full max-w-lg bg-[#0e0e12]/95 border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
            variants={modalVariant}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Gradient accent top bar */}
            <div className="h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/15 transition-all text-gray-400 hover:text-white z-10"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Step indicator */}
            {(step === 'signup' || step === 'apply') && (
              <div className="flex items-center justify-center gap-3 pt-6 px-6">
                <div className={`flex items-center gap-2 text-xs font-medium ${step === 'signup' ? 'text-emerald-400' : 'text-gray-500'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${step === 'signup' ? 'bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/40' : 'bg-white/5 text-gray-500'}`}>
                    {step === 'apply' ? <CheckCircle2 className="w-3.5 h-3.5" /> : '১'}
                  </div>
                  সাইনআপ
                </div>
                <div className="w-8 h-px bg-white/10" />
                <div className={`flex items-center gap-2 text-xs font-medium ${step === 'apply' ? 'text-emerald-400' : 'text-gray-500'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${step === 'apply' ? 'bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/40' : 'bg-white/5 text-gray-500'}`}>
                    ২
                  </div>
                  আবেদন
                </div>
              </div>
            )}

            <div className="p-6 pt-4">
              <AnimatePresence mode="wait">
                {/* ============ STEP 1: SIGNUP ============ */}
                {step === 'signup' && (
                  <motion.div key="signup" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                    <div className="text-center mb-6">
                      <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/20 flex items-center justify-center">
                        <GraduationCap className="w-7 h-7 text-emerald-400" />
                      </div>
                      <h2 className="text-lg font-extrabold text-white">শিক্ষক হিসেবে যোগ দিন</h2>
                      <p className="text-xs text-gray-500 mt-1">ধাপ ১: আপনার অ্যাকাউন্ট তৈরি করুন</p>
                      <button
                        onClick={handleAutofill}
                        type="button"
                        className="mt-2 text-[10px] font-bold px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 hover:bg-emerald-500/25 transition-all flex items-center gap-1 mx-auto cursor-pointer"
                      >
                        <Sparkles className="w-3 h-3 animate-pulse" /> Autofill Demo
                      </button>
                    </div>

                    {error && (
                      <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-red-300">{error}</p>
                      </div>
                    )}

                    <div className="space-y-3">
                      {/* Name */}
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input
                          type="text" placeholder="পূর্ণ নাম"
                          value={name} onChange={(e) => setName(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition-all"
                        />
                      </div>
                      {/* Email */}
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input
                          type="email" placeholder="ইমেইল ঠিকানা"
                          value={email} onChange={(e) => setEmail(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition-all"
                        />
                      </div>
                      {/* Phone */}
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input
                          type="tel" placeholder="ফোন নম্বর (01XXXXXXXXX)"
                          value={phone} onChange={(e) => setPhone(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition-all"
                        />
                      </div>
                      {/* Password */}
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input
                          type={showPassword ? 'text' : 'password'} placeholder="পাসওয়ার্ড (কমপক্ষে ৬ অক্ষর)"
                          value={password} onChange={(e) => setPassword(e.target.value)}
                          className="w-full pl-10 pr-10 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition-all"
                        />
                        <button onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors">
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <button
                      onClick={handleStep1Submit}
                      disabled={loading}
                      className="w-full mt-5 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-sm font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><span>পরবর্তী ধাপ</span> <ArrowRight className="w-4 h-4" /></>}
                    </button>
                  </motion.div>
                )}

                {/* ============ STEP 2: APPLY ============ */}
                {step === 'apply' && (
                  <motion.div key="apply" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                    <div className="text-center mb-6">
                      <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/20 flex items-center justify-center">
                        <FileText className="w-7 h-7 text-cyan-400" />
                      </div>
                      <h2 className="text-lg font-extrabold text-white">শিক্ষক আবেদন ফর্ম</h2>
                      <p className="text-xs text-gray-500 mt-1">ধাপ ২: আপনার পেশাগত তথ্য দিন</p>
                      <button
                        onClick={handleAutofill}
                        type="button"
                        className="mt-2 text-[10px] font-bold px-2.5 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/25 text-cyan-400 hover:bg-cyan-500/25 transition-all flex items-center gap-1 mx-auto cursor-pointer"
                      >
                        <Sparkles className="w-3 h-3 animate-pulse" /> Autofill Demo
                      </button>
                    </div>

                    {error && (
                      <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-red-300">{error}</p>
                      </div>
                    )}

                    <div className="space-y-3">
                      {/* Subject */}
                      <div className="relative">
                        <BookOpen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <select
                          value={subject} onChange={(e) => setSubject(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition-all appearance-none"
                        >
                          <option value="" className="bg-[#1a1a2e]">বিষয় নির্বাচন করুন *</option>
                          {subjectOptions.map(s => (
                            <option key={s.value} value={s.value} className="bg-[#1a1a2e]">{s.label}</option>
                          ))}
                        </select>
                      </div>
                      {/* Institution */}
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input
                          type="text" placeholder="প্রতিষ্ঠানের নাম *"
                          value={institution} onChange={(e) => setInstitution(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition-all"
                        />
                      </div>
                      {/* Experience */}
                      <div className="relative">
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <select
                          value={experience} onChange={(e) => setExperience(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition-all appearance-none"
                        >
                          <option value="" className="bg-[#1a1a2e]">শিক্ষকতার অভিজ্ঞতা</option>
                          {experienceOptions.map(e => (
                            <option key={e.value} value={e.value} className="bg-[#1a1a2e]">{e.label}</option>
                          ))}
                        </select>
                      </div>
                      {/* Qualification */}
                      <div className="relative">
                        <Award className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input
                          type="text" placeholder="শিক্ষাগত যোগ্যতা (যেমন: B.Sc in Physics)"
                          value={qualification} onChange={(e) => setQualification(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition-all"
                        />
                      </div>
                      {/* Bio */}
                      <textarea
                        placeholder="আপনার সম্পর্কে সংক্ষেপে লিখুন..."
                        value={bio} onChange={(e) => setBio(e.target.value)}
                        rows={3}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition-all resize-none"
                      />
                    </div>

                    <div className="flex gap-3 mt-5">
                      <button
                        onClick={() => { setStep('signup'); setError(''); }}
                        className="flex-1 py-3 rounded-xl bg-white/5 border border-white/10 text-gray-400 text-sm font-medium flex items-center justify-center gap-2 hover:bg-white/10 transition-all"
                      >
                        <ArrowLeft className="w-4 h-4" /> পেছনে
                      </button>
                      <button
                        onClick={handleStep2Submit}
                        disabled={loading}
                        className="flex-[2] py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-sm font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><span>আবেদন জমা দিন</span> <CheckCircle2 className="w-4 h-4" /></>}
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* ============ SUCCESS STATE ============ */}
                {step === 'success' && (
                  <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
                    <div className="text-center py-6">
                      <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 flex items-center justify-center">
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.2, type: 'spring', stiffness: 400, damping: 15 }}
                        >
                          <Sparkles className="w-10 h-10 text-emerald-400" />
                        </motion.div>
                      </div>
                      <h2 className="text-xl font-extrabold text-white mb-2">আবেদন সফল!</h2>
                      <p className="text-sm text-gray-400 leading-relaxed max-w-xs mx-auto">
                        আপনার শিক্ষক আবেদন সফলভাবে জমা হয়েছে। অ্যাডমিন পর্যালোচনা ও অনুমোদনের পর আপনি শিক্ষক ড্যাশবোর্ড ব্যবহার করতে পারবেন।
                      </p>
                      <div className="mt-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 inline-flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-400" />
                        <span className="text-xs text-amber-300 font-medium">অনুমোদনের অপেক্ষায় আছে</span>
                      </div>
                      <button
                        onClick={handleClose}
                        className="w-full mt-6 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm font-medium hover:bg-white/10 transition-all"
                      >
                        বন্ধ করুন
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
